import React, { useState, useEffect, useCallback } from 'react';

const WordGuess = ({ onGameWin, onGameStart, isDarkMode }) => {
  const WORD_LIST = [
    'REACT', 'BRAIN', 'GHOST', 'PIANO', 'GRAPE', 'FLAME', 'CROWN', 'MIXER',
    'BREAD', 'SWIFT', 'FROST', 'CRISP', 'BLEND', 'CRAFT', 'SPICE', 'BLAZE',
    'CHESS', 'PLUMB', 'TRUNK', 'SHARK', 'STORM', 'BURST', 'PEARL', 'RIDGE',
    'CHARM', 'PRISM', 'GLAZE', 'MAPLE', 'DANCE', 'MAGIC', 'BLOCK', 'SMART',
    'TREND', 'SCOPE', 'GLOBE', 'MARCH', 'SPARK', 'TRUST', 'SPORT', 'LIGHT',
    'STONE', 'PRIZE', 'GRAPE', 'QUICK', 'BROWN', 'JUMPS', 'CLIMB', 'THREW',
    'YOUTH', 'PLANT', 'FLASH', 'BRAVE', 'GLIDE', 'SHADE', 'BRING', 'SHINE'
  ];

  const [targetWord, setTargetWord] = useState('');
  const [guesses, setGuesses] = useState([]);
  const [currentGuess, setCurrentGuess] = useState('');
  const [gameStatus, setGameStatus] = useState('intro'); // intro, playing, won, lost
  const [message, setMessage] = useState('Guess the 5-letter word!');
  const [startTime, setStartTime] = useState(null);
  const [sessionId, setSessionId] = useState(null);

  // Start game
  const startGame = useCallback(async () => {
    const randomWord = WORD_LIST[Math.floor(Math.random() * WORD_LIST.length)];
    setTargetWord(randomWord);
    setGuesses([]);
    setCurrentGuess('');
    setGameStatus('playing');
    setMessage('Guess the 5-letter word!');
    setStartTime(Date.now());
    
    // Create game session when starting
    if (onGameStart) {
      const newSessionId = await onGameStart('wordGuess');
      setSessionId(newSessionId);
    }
  }, [onGameStart]);

  const getLetterStatus = (letter, position, guess) => {
    if (targetWord[position] === letter) {
      return 'correct'; // Green - correct position
    } else if (targetWord.includes(letter)) {
      return 'present'; // Yellow - wrong position
    } else {
      return 'absent'; // Gray - not in word
    }
  };

  const handleSubmitGuess = useCallback(() => {
    if (currentGuess.length !== 5) {
      setMessage('Word must be 5 letters!');
      return;
    }

    if (!/^[A-Z]+$/.test(currentGuess)) {
      setMessage('Only letters allowed!');
      return;
    }

    const newGuesses = [...guesses, currentGuess];
    setGuesses(newGuesses);
    setCurrentGuess('');

    if (currentGuess === targetWord) {
      // Won!
      setGameStatus('won');
      setMessage('🎉 You got it!');
      
      setTimeout(() => onGameWin(sessionId), 1000);
    } else if (newGuesses.length >= 6) {
      // Lost - used all 6 guesses
      setGameStatus('lost');
      setMessage(`Game Over! The word was ${targetWord}`);
    } else {
      setMessage(`${6 - newGuesses.length} guesses left`);
    }
  }, [currentGuess, guesses, targetWord, sessionId, onGameWin]);

  const handleKeyPress = useCallback((e) => {
    if (gameStatus !== 'playing') return;

    if (e.key === 'Enter') {
      handleSubmitGuess();
    } else if (e.key === 'Backspace') {
      setCurrentGuess(prev => prev.slice(0, -1));
    } else if (/^[a-zA-Z]$/.test(e.key) && currentGuess.length < 5) {
      setCurrentGuess(prev => prev + e.key.toUpperCase());
    }
  }, [gameStatus, currentGuess, handleSubmitGuess]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handleKeyPress]);

  const handleLetterClick = (letter) => {
    if (gameStatus !== 'playing') return;
    if (currentGuess.length < 5) {
      setCurrentGuess(prev => prev + letter);
    }
  };

  const handleBackspace = () => {
    setCurrentGuess(prev => prev.slice(0, -1));
  };

  const renderGuessRow = (guess, rowIndex) => {
    const letters = guess ? guess.split('') : Array(5).fill('');
    const isCurrentRow = rowIndex === guesses.length && !guess;

    return (
      <div key={rowIndex} className="flex gap-1 md:gap-2 justify-center">
        {letters.map((letter, i) => {
          const status = guess ? getLetterStatus(letter, i, guess) : '';
          const isCurrentLetter = isCurrentRow && i === currentGuess.length;
          
          return (
            <div
              key={i}
              className={`
                w-10 h-10 md:w-16 md:h-16 border-2 flex items-center justify-center text-lg md:text-3xl font-bold rounded
                ${status === 'correct' ? 'bg-green-500 border-green-600 text-white' : ''}
                ${status === 'present' ? 'bg-yellow-500 border-yellow-600 text-white' : ''}
                ${status === 'absent' ? 'bg-gray-500 border-gray-600 text-white' : ''}
                ${!status && letter ? (isDarkMode ? 'border-gray-400 bg-transparent' : 'border-gray-400 bg-white') : ''}
                ${!status && !letter ? (isDarkMode ? 'border-gray-600 bg-transparent' : 'border-gray-300 bg-white') : ''}
                ${isCurrentLetter ? 'border-blue-500' : ''}
              `}
            >
              <span style={!status && letter ? { color: isDarkMode ? 'white' : 'black' } : {}}>{letter}</span>
            </div>
          );
        })}
      </div>
    );
  };

  const renderCurrentGuessRow = () => {
    if (guesses.length >= 6 || gameStatus !== 'playing') return null;

    const letters = currentGuess.split('');
    while (letters.length < 5) letters.push('');

    return (
      <div className="flex gap-1 md:gap-2 justify-center">
        {letters.map((letter, i) => (
          <div
            key={i}
            className={`
              w-10 h-10 md:w-16 md:h-16 border-2 flex items-center justify-center text-lg md:text-3xl font-bold rounded
              ${letter ? (isDarkMode ? 'border-gray-400' : 'border-gray-400') : (isDarkMode ? 'border-gray-600' : 'border-gray-300')}
              ${i === currentGuess.length ? 'border-blue-500' : ''}
            `}
          >
            <span style={letter ? { color: isDarkMode ? 'white' : 'black' } : {}}>{letter}</span>
          </div>
        ))}
      </div>
    );
  };

  const KEYBOARD_ROWS = [
    ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
    ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'],
    ['⌫', 'Z', 'X', 'C', 'V', 'B', 'N', 'M', 'ENTER']
  ];

  const getKeyStatus = (letter) => {
    let status = '';
    guesses.forEach(guess => {
      guess.split('').forEach((l, i) => {
        if (l === letter) {
          const letterStatus = getLetterStatus(l, i, guess);
          if (letterStatus === 'correct') status = 'correct';
          else if (letterStatus === 'present' && status !== 'correct') status = 'present';
          else if (letterStatus === 'absent' && !status) status = 'absent';
        }
      });
    });
    return status;
  };

  return (
    <div className="flex flex-col items-center space-y-2 md:space-y-6 p-1 md:p-4">
      <div className="text-center space-y-0.5">
        <h2 className="text-lg md:text-2xl font-bold text-purple-400">Word Guess</h2>
        <p className="text-[10px] md:text-sm text-gray-400">Guess the 5-letter word!</p>
      </div>

      {/* Intro Screen */}
      {gameStatus === 'intro' && (
        <div className="text-center max-w-md space-y-4">
          <p className="text-base md:text-lg text-gray-300">
            Guess the word in 6 tries to win 15 SubPoints!
          </p>
          <button
            onClick={startGame}
            className="px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white text-base md:text-lg font-bold rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            Start Game
          </button>
        </div>
      )}

      {/* Game Screen */}
      {gameStatus !== 'intro' && (
        <>
          <div className="bg-gray-800 px-2 py-1 md:px-4 md:py-2 rounded-lg border border-gray-700">
            <p className="text-xs md:text-base text-gray-300 font-semibold">{message}</p>
            <p className="text-[10px] md:text-xs text-gray-500">
              {gameStatus === 'playing' ? `Attempt ${guesses.length + 1}/6` : ''}
            </p>
          </div>

          {/* Game Board */}
          <div className="space-y-1 md:space-y-2">
            {guesses.map((guess, i) => renderGuessRow(guess, i))}
            {renderCurrentGuessRow()}
            {/* Empty rows */}
            {Array.from({ length: Math.max(0, 6 - guesses.length - (gameStatus === 'playing' ? 1 : 0)) }).map((_, i) => (
              <div key={`empty-${i}`} className="flex gap-1 md:gap-2 justify-center">
                {Array(5).fill('').map((_, j) => (
                  <div key={j} className="w-10 h-10 md:w-16 md:h-16 border-2 border-gray-300 rounded" />
                ))}
              </div>
            ))}
          </div>

          {/* On-screen Keyboard */}
          <div className="w-full space-y-1 md:space-y-2 mt-1 px-1">
            {KEYBOARD_ROWS.map((row, rowIndex) => (
              <div key={rowIndex} className="flex gap-1 md:gap-1.5 justify-center">
                {row.map((key) => {
                  const status = key.length === 1 ? getKeyStatus(key) : '';
                  const isSpecial = key === 'ENTER' || key === '⌫';
                  
                  return (
                    <button
                      key={key}
                      onClick={() => {
                        if (key === 'ENTER') handleSubmitGuess();
                        else if (key === '⌫') handleBackspace();
                        else handleLetterClick(key);
                      }}
                      disabled={gameStatus !== 'playing'}
                      className={`
                        flex-1 ${isSpecial ? 'max-w-[60px]' : 'max-w-[40px]'} md:px-3 py-2.5 md:py-4 rounded font-bold text-sm md:text-base
                        ${status === 'correct' ? 'bg-green-500 text-white' : ''}
                        ${status === 'present' ? 'bg-yellow-500 text-white' : ''}
                        ${status === 'absent' ? 'bg-gray-400 text-white' : ''}
                        ${!status ? 'bg-gray-200 text-gray-800' : ''}
                        ${gameStatus === 'playing' ? 'hover:bg-opacity-80 active:scale-95' : 'opacity-50 cursor-not-allowed'}
                        transition-all duration-150
                      `}
                    >
                      {key}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>

          <div className="text-center text-xs text-gray-400">
            <p>🟩 Correct · 🟨 Wrong spot · ⬜ Not in word</p>
          </div>

          {/* Win/Loss Messages */}
          {gameStatus === 'won' && (
            <div className="bg-yellow-900/50 border-2 border-yellow-500 p-4 md:p-6 rounded-xl text-center animate-pulse">
              <p className="text-xl md:text-2xl font-bold text-yellow-400">🎉 You Win! 🎉</p>
              <p className="text-sm md:text-base text-gray-300">You earned 15 Points</p>
            </div>
          )}

          {gameStatus === 'lost' && (
            <div className="bg-red-900/50 border-2 border-red-500 p-4 md:p-6 rounded-xl text-center">
              <p className="text-xl md:text-2xl font-bold text-red-400 mb-1 md:mb-2">Game Over!</p>
              <p className="text-sm md:text-base text-gray-300">The word was: {targetWord}</p>
              <button
                onClick={startGame}
                className="mt-3 md:mt-4 px-4 py-2 md:px-6 md:py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold transition-colors text-sm md:text-base"
              >
                Play Again
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default WordGuess;
